<script setup lang="ts">
import type {
  TpgBlurEvent,
  TpgChangeEvent,
  TpgClickEvent,
  TpgFocusEvent,
  TpgInputEvent,
} from '@tpg/grammar-web';
import type { IconConstant } from '@tpg/grammar-web-icons';
import { computed, ref } from 'vue';

import { omit } from '@nora-monorepo/shared';

export interface GrammarTextFieldProps {
  label?: string;
  uom?: string;
  placeholder?: string;
  value?: string | Date;
  counter?: number;
  type?: 'default' | 'error' | 'warning' | 'danger';
  disabled?: boolean;
  readOnly?: boolean;
  required?: boolean;
  truncate?: boolean;
  autoMark?: boolean;
  clearButton?: boolean;
  leadingIconTemplate?: IconConstant | '';
  trailingIconTemplate?: IconConstant | '';
  message?: string;
  inputType?: 'text' | 'password' | 'number' | 'date';
  formAssociated?: boolean;
  name?: string;

  // Custom properties not supported by grammar-web
  min?: number | Date;
  max?: number | Date;
}

export interface GrammarTextFieldExpose {
  focus: () => void;
}

const props = withDefaults(defineProps<GrammarTextFieldProps>(), {
  label: '',
  uom: '',
  placeholder: '',
  value: '',
  counter: undefined,
  type: 'default',
  disabled: false,
  readOnly: false,
  required: false,
  truncate: false,
  autoMark: false,
  clearButton: false,
  leadingIconTemplate: '',
  trailingIconTemplate: '',
  message: '',
  inputType: 'text',
  formAssociated: true,
  name: '',

  // Custom properties not supported by grammar-web
  min: undefined,
  max: undefined,
});

const emit = defineEmits<{
  (event: 'blur', data: TpgBlurEvent): void;
  (event: 'change', value: string): void;
  (event: 'click', data: TpgClickEvent): void;
  (event: 'focus', data: TpgFocusEvent): void;
  (event: 'input', value: string): void;
  (event: 'keydown', data: KeyboardEvent): void;
  (event: 'update:value', value: string): void;
}>();

const textFieldRef = ref();

const webComponentProps = computed(() => {
  const supportedProps = omit(props, [
    'min',
    'max',
  ]);
  return Object.assign(supportedProps, {
    value: props.value,
  });
});

function onInput(event: TpgInputEvent<string>) {
  let value = event.value ?? '';

  if (
    props.inputType === 'number'
  ) {
    const parsedValue = Number.parseFloat(value);
    if (!Number.isNaN(parsedValue)) {
      if (
        typeof props.min === 'number'
        && parsedValue < props.min
      ) {
        value = props.min.toString();
      }

      if (
        typeof props.max === 'number'
        && parsedValue > props.max
      ) {
        value = props.max.toString();
      }
    }
  }

  emit('input', value);
  emit('update:value', value);
}

function onBlur(event: TpgBlurEvent) {
  emit('blur', event);
}

function onClick(event: TpgClickEvent) {
  emit('click', event);
}

function onFocus(event: TpgFocusEvent) {
  emit('focus', event);
}

function onKeydown(event: KeyboardEvent) {
  emit('keydown', event);
}

function onClearButtonClick(event: TpgChangeEvent<string>) {
  if (props.clearButton && event.value === '') {
    emit('change', '');
  }
}

function focus() {
  textFieldRef.value?.focus();
}

defineExpose<GrammarTextFieldExpose>({
  focus,
});
</script>

<template>
  <tpg-text-field
    ref="textFieldRef"
    v-bind="webComponentProps"
    @tpg-input="onInput"
    @tpg-blur="onBlur"
    @tpg-focus="onFocus"
    @tpg-change="onClearButtonClick"
    @tpg-click="onClick"
    @keydown="onKeydown"
  />
</template>
